import { useNavigate, useSearch } from "@tanstack/react-router";
import { DrawerId as CCDrawerId } from "components/CC/types/models";
import { useEffect, useRef } from "react";
import { DrawerId } from "types/models";
import { markDrawerNavigationBypass } from "utils/drawerNavigationBypass";
import { setPendingDrawerVisibility } from "utils/drawerVisibilityStore";

// Accept both global DrawerId and feature specific DrawerId
type AnyDrawerId = DrawerId | CCDrawerId;

interface SearchParams {
    drawerIds?: string;
    [key: string]: unknown;
}

const DRAWER_URL_SYNC_DELAY_MS = 350;
const pendingDrawerNavigationTimeouts = new Map<string, number>();

const deferDrawerNavigation = (drawerId: string, callback: () => void) => {
    const existingTimeoutId = pendingDrawerNavigationTimeouts.get(drawerId);

    if (existingTimeoutId !== undefined) {
        window.clearTimeout(existingTimeoutId);
    }

    const timeoutId = window.setTimeout(() => {
        pendingDrawerNavigationTimeouts.delete(drawerId);
        callback();
    }, DRAWER_URL_SYNC_DELAY_MS);

    pendingDrawerNavigationTimeouts.set(drawerId, timeoutId);
};

export const useDrawer = () => {
    const searchParams = useSearch({ strict: false }) as SearchParams;
    const navigate = useNavigate();
    const searchParamsRef = useRef(searchParams);

    useEffect(() => {
        searchParamsRef.current = searchParams;
    }, [searchParams]);

    // Helper function to get all active drawers
    const getAllDrawers = (): string[] => {
        const drawerIds = searchParamsRef.current.drawerIds;
        return drawerIds ? drawerIds.split(",") : [];
    };

    // Add drawerId to query params
    const showDrawer = (drawerId: AnyDrawerId, search?: Record<string, unknown>) => {
        const currentDrawerIds = getAllDrawers();
        const isAlreadyActive = currentDrawerIds.includes(drawerId.toString());
        const updatedDrawerIds = isAlreadyActive
            ? currentDrawerIds.join(",")
            : [...currentDrawerIds, drawerId.toString()].join(",");
        const hasSearchUpdates = Boolean(search && Object.keys(search).length > 0);

        if (!isAlreadyActive || hasSearchUpdates) {
            const drawerIdString = drawerId.toString();
            markDrawerNavigationBypass(window.location.pathname);
            setPendingDrawerVisibility(drawerIdString, true);

            deferDrawerNavigation(drawerIdString, () => {
                navigate({
                    to: ".",
                    search: ((prev: Record<string, unknown>) => ({
                        ...prev,
                        ...search,
                        drawerIds: updatedDrawerIds,
                    })) as never,
                    replace: true,
                });
            });
        }
    };

    // Remove drawerId from query params
    const hideDrawer = (drawerId: AnyDrawerId, searchKeys?: string | string[]) => {
        const drawerIdString = drawerId.toString();
        const currentDrawerIds = getAllDrawers();
        const updatedDrawerIds = currentDrawerIds.filter((id) => id !== drawerIdString);

        const updatedSearchParams: SearchParams = {
            ...searchParamsRef.current,
            drawerIds: updatedDrawerIds.join(","),
        };

        if (searchKeys) {
            // Convert searchKeys to an array if it is a string
            const keys = Array.isArray(searchKeys) ? searchKeys : [searchKeys];
            // Remove each key provided in searchKeys from updatedSearchParams
            keys.forEach((key) => {
                delete updatedSearchParams[key];
            });
        }

        if (updatedDrawerIds.length === 0) {
            delete updatedSearchParams.drawerIds;
        }

        markDrawerNavigationBypass(window.location.pathname);
        setPendingDrawerVisibility(drawerIdString, false);

        deferDrawerNavigation(drawerIdString, () => {
            navigate({
                to: ".",
                search: (() => updatedSearchParams) as never,
                replace: true,
            });
        });
    };

    // Check if drawer is active
    const isDrawerActive = (drawerId: AnyDrawerId): boolean => {
        const currentDrawerIds = getAllDrawers();
        return currentDrawerIds.includes(drawerId.toString());
    };

    // Toggle the drawer visibility
    const toggleDrawer = (drawerId: AnyDrawerId, search?: Record<string, unknown>) => {
        if (isDrawerActive(drawerId)) {
            hideDrawer(drawerId, Object.keys(search || {}).toString());
        } else {
            showDrawer(drawerId, search);
        }
    };

    // Generate URL with drawerId added
    const getShowDrawerUrl = (drawerId: AnyDrawerId, originUrl: string): string => {
        const currentDrawerIds = getAllDrawers();
        const updatedDrawerIds = [...currentDrawerIds, drawerId.toString()].join(",");
        const url = new URL(originUrl);
        url.searchParams.set("drawerIds", updatedDrawerIds);
        return url.toString();
    };

    // Generate URL with drawerId removed
    const getHideDrawerUrl = (drawerId: AnyDrawerId, originUrl: string): string => {
        const currentDrawerIds = getAllDrawers();
        const updatedDrawerIds = currentDrawerIds.filter((id) => id !== drawerId.toString()).join(",");
        const url = new URL(originUrl);
        if (updatedDrawerIds.length > 0) {
            url.searchParams.set("drawerIds", updatedDrawerIds);
        } else {
            url.searchParams.delete("drawerIds");
        }
        return url.toString();
    };

    // Generate toggle URL for drawer
    const getToggleDrawerUrl = (drawerId: AnyDrawerId, originUrl: string): string => {
        if (isDrawerActive(drawerId)) {
            return getHideDrawerUrl(drawerId, originUrl);
        } else {
            return getShowDrawerUrl(drawerId, originUrl);
        }
    };

    return {
        show: showDrawer,
        getShowUrl: getShowDrawerUrl,
        hide: hideDrawer,
        getHideUrl: getHideDrawerUrl,
        toggle: toggleDrawer,
        getToggleUrl: getToggleDrawerUrl,
        isDrawerActive,
        list: getAllDrawers,
    };
};
